import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { useCallback } from 'react';
import { FieldArray } from 'react-final-form-arrays';

import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmErknmFormApproveDoc } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormApproveDoc';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAcceptedDocument } from 'APIPackage/DictionariesModule/interfaces/IAcceptedDocument';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { IPermitDocs } from '../../../APIPackage/DictionariesModule/interfaces/IPermitDocs';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { IKnmErknmFormDocTarget } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormDocTarget';
import { IKnmFormRef } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataCreated,
  getKnmErknmAcceptedDocumentsSelectOptions,
  getKnmFormRef,
  getPermitDocsSelectOptions,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import {
  DOC_TARGET_BLOCK_ID,
  getApproveDocBlockId,
} from '../../../KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IDictionarySupervisionTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { KnmApproveDocs } from '../../../KnmPackage/KnmEventSubjectModule/components/KnmApproveDocs';
import {
  getDocTargetOptions,
  getDocTargetType,
} from '../../../KnmPackage/KnmEventSubjectModule/common/docTypeGetFunctions';

interface IPmApproveDocsProps {
  watchOnly: boolean;
  supervisionType?: IDictionarySupervisionTypeSelectOption;
}

interface IPmApproveDocsStoreProps {
  formRef: IKnmFormRef | undefined;
  settings: IAdminSetting[];
  docOptionsOld: IDictionarySelectOption<IAcceptedDocument>[] | undefined;
  docOptions: IDictionarySelectOption<IPermitDocs>[] | undefined;
  created: string | null | undefined;
}

const PmApproveDocsWithoutStore = ({
  watchOnly,
  formRef,
  docOptions,
  supervisionType,
  created,
  settings,
  docOptionsOld,
}: IPmApproveDocsStoreProps & IPmApproveDocsProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const approveDocsFieldName = 'approveDocs' as keyof IKnmForm;

  const reasonsPm = formRef?.current.getFieldState('reasonsPm')?.value;
  const isImpossibilityOfPrevious = reasonsPm?.[0]?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS;

  const isEnabled = !watchOnly && !isImpossibilityOfPrevious;

  /**
   * Добавление нового элемента
   */
  const addField = useCallback(() => {
    if (isEnabled && push) {
      const formDoc: IKnmErknmFormDocTarget = {
        ervkControlKindDocTarget: undefined,
        specificRequirementAndClause: undefined,
        file: undefined,
        listId: uuidv4(),
        docTargetType: getDocTargetType(docOptions),
        guid: undefined,
      };

      const docTarget = formRef.current.getFieldState('docTarget')?.value;
      const selectedFields = docTarget?.map(field => field?.ervkControlKindDocTarget?.targetTitle) || [];

      const options = getDocTargetOptions(selectedFields, formDoc, docOptions);

      formDoc.ervkControlKindDocTarget = options?.length === 1 ? options[0] : undefined;

      push('docTarget', formDoc);
    }
  }, [push, isEnabled, docOptions, formRef]);

  /**
   * Добавление нового элемента (старая версия)
   */
  const addFieldOld = useCallback(() => {
    const addingField: IKnmErknmFormApproveDoc = {
      approveDoc: undefined,
      dateCreate: undefined,
      numGuid: undefined,
      listId: uuidv4(),
    };

    if (isEnabled && push) push(approveDocsFieldName, addingField);
  }, [push, isEnabled]);

  /**
   * Удаляет поля документа (старая версия)
   */
  const removeFieldOld = useCallback(
    (index: number) => {
      if (isEnabled && remove) remove(approveDocsFieldName, index);
    },
    [remove, isEnabled],
  );

  const isShow26 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_26_STARTS_FROM);

  const isEnabledBy481415 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PERMITTED_DOC_481_4_1_5_STARTS_FROM,
  );

  if (isShow26 && isEnabledBy481415 && docOptions && docOptions.length > 0) {
    return (
      <FieldBlock
        label="Разрешительные и иные документы"
        id={DOC_TARGET_BLOCK_ID}
        button={
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            onClick={addField}
            disabled={!isEnabled || !supervisionType}
          />
        }
      >
        <FieldArray name={'docTarget'}>
          {({ fields }) => {
            if (!fields.length) {
              return null;
            }

            return (
              <Row>
                {fields.map((fieldName, sIndex) => {
                  const fieldValue: IKnmErknmFormDocTarget = fields.value?.[sIndex];
                  const id = getApproveDocBlockId(fieldValue.listId);
                  const selectedFields = fields.value.map(field => field?.ervkControlKindDocTarget?.targetTitle);

                  return (
                    <KnmApproveDocs
                      id={id}
                      isWatchOnly={!isEnabled}
                      docOptions={docOptions}
                      fieldName={fieldName}
                      fieldValue={fieldValue}
                      selectedFields={selectedFields}
                      sIndex={sIndex}
                      key={id}
                    />
                  );
                })}
              </Row>
            );
          }}
        </FieldArray>
      </FieldBlock>
    );
  }

  if (!isShow26) {
    return (
      <FieldBlock
        label="Разрешительные и иные документы"
        button={
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            onClick={addFieldOld}
            disabled={!isEnabled}
          />
        }
      >
        <FieldArray name={approveDocsFieldName}>
          {({ fields }) => {
            if (!fields.length) return null;

            return (
              <Row>
                {fields.map((fieldName, sIndex) => {
                  const fieldValue: IKnmErknmFormApproveDoc = fields.value?.[sIndex];
                  const id = getApproveDocBlockId(fieldValue.listId);

                  return (
                    <Row key={id} id={id} margin="medium" isListItem>
                      <SelectInputField
                        className="shared-deleting-row"
                        id={`${fieldName}.approveDoc`}
                        name={`${fieldName}.approveDoc`}
                        placeholder="Выберите документ"
                        isClearable={false}
                        size="medium"
                        options={docOptionsOld}
                        disabled={!isEnabled}
                      />
                      {isEnabled && (
                        <CloseButton className="shared-deleting-row-btn" onClick={() => removeFieldOld(sIndex)} />
                      )}
                    </Row>
                  );
                })}
              </Row>
            );
          }}
        </FieldArray>
      </FieldBlock>
    );
  }

  return <></>;
};

const mapStateToProps = (state: IRootState): IPmApproveDocsStoreProps => ({
  formRef: getKnmFormRef(state),
  settings: getAdminFetchSettings(state),
  docOptionsOld: getKnmErknmAcceptedDocumentsSelectOptions(state),
  docOptions: getPermitDocsSelectOptions(state),
  created: getKnmDataCreated(state),
});

export const PmApproveDocs = connect(mapStateToProps)(PmApproveDocsWithoutStore);
